import React, { useState, useEffect, useRef, useCallback } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { Container, Button, Form, Badge, Spinner } from 'react-bootstrap'
import Select from 'react-select'
import DatePicker from 'react-datepicker'
import "react-datepicker/dist/react-datepicker.css"
import ResizableTable from '../components/ResizableTable'
import PaginationComponent from '../components/Pagination'
import { amazonAPI } from '../api/amazonAPI'
import { formatDateOnly } from '../utils/commonUtils'
import { formatErrorResponse } from '../utils/errorHandlers'
import { usePermissions } from '../contexts/PermissionContext'
import '../styles/selectControl.css'

function AmazonEconomicsListScreen() {
    const { hasPermission } = usePermissions()
    const navigate = useNavigate()
    const location = useLocation()

    const urlParams = new URLSearchParams(location.state?.returnFilters || location.search)

    const [accounts, setAccounts] = useState([])
    const [selectedAccountId, setSelectedAccountId] = useState(() => urlParams.get('account') || '')
    const [error, setError] = useState('')

    const [startDate, setStartDate] = useState(null)
    const [endDate, setEndDate] = useState(null)
    const [excludeInactive, setExcludeInactive] = useState(true)
    const [submitting, setSubmitting] = useState(false)
    const [queries, setQueries] = useState([])
    const [queriesLoading, setQueriesLoading] = useState(false)
    const autoRefreshRef = useRef(null)
    const [activeQuickDate, setActiveQuickDate] = useState(null)

    const [currentPage, setCurrentPage] = useState(() => parseInt(urlParams.get('page')) || 1)
    const [totalItems, setTotalItems] = useState(0)
    const [pageSize, setPageSize] = useState(PaginationComponent.getCurrentPageSize())
    const [filterStartDate, setFilterStartDate] = useState(() => urlParams.get('filterStart') || '')
    const [filterEndDate, setFilterEndDate] = useState(() => urlParams.get('filterEnd') || '')
    const [sortDirection, setSortDirection] = useState(() => urlParams.get('sortDir') || 'desc')
    const [sourceFilter, setSourceFilter] = useState(() => urlParams.get('source') || '')

    useEffect(() => {
        if (location.state?.returnFilters) {
            navigate(`${location.pathname}${location.state.returnFilters}`, {
                replace: true, state: {}
            })
        }
    }, [])

    useEffect(() => {
        const fetchAccounts = async () => {
            try {
                const data = await amazonAPI.accounts.getAll()
                if (data) {
                    const active = data.filter(a => a.is_active)
                    setAccounts(active)
                    if (active.length === 1 && !selectedAccountId) {
                        setSelectedAccountId(active[0].id)
                    }
                }
            } catch (err) {
                console.error('Failed to fetch accounts:', err)
            }
        }
        fetchAccounts()
    }, [])

    useEffect(() => {
        const params = new URLSearchParams()
        if (selectedAccountId) params.set('account', selectedAccountId)
        if (filterStartDate) params.set('filterStart', filterStartDate)
        if (filterEndDate) params.set('filterEnd', filterEndDate)
        if (currentPage > 1) params.set('page', currentPage)
        if (sortDirection !== 'desc') params.set('sortDir', sortDirection)
        if (sourceFilter) params.set('source', sourceFilter)
        const search = params.toString()
        navigate(`${location.pathname}${search ? '?' + search : ''}`, { replace: true })
    }, [selectedAccountId, filterStartDate, filterEndDate, currentPage, sortDirection, sourceFilter])

    const accountOptions = accounts.map(a => ({ value: a.id, label: a.account_name }))

    const getStatusBadge = (st) => {
        const map = { SUBMITTED: 'warning', PROCESSING: 'warning', DONE: 'success', FAILED: 'danger' }
        return <Badge bg={map[st] || 'secondary'}>{st}</Badge>
    }

    const fetchQueries = useCallback(async () => {
        setQueriesLoading(true)
        try {
            const params = { page: currentPage, pageSize, sortDirection }
            if (selectedAccountId) params.account_id = selectedAccountId
            if (filterStartDate) params.filter_start_date = filterStartDate
            if (filterEndDate) params.filter_end_date = filterEndDate
            if (sourceFilter) params.collection_type = sourceFilter

            const data = await amazonAPI.queries.getAll(params)
            if (data && data.results !== undefined) {
                setQueries(data.results)
                setTotalItems(data.count)
            } else {
                setQueries(data || [])
                setTotalItems((data || []).length)
            }
        } catch (err) {
            console.error('Failed to fetch queries:', err)
        } finally {
            setQueriesLoading(false)
        }
    }, [selectedAccountId, currentPage, pageSize, filterStartDate, filterEndDate, sortDirection, sourceFilter])

    useEffect(() => { fetchQueries() }, [fetchQueries])

    useEffect(() => {
        const hasActive = queries.some(q => ['SUBMITTED', 'PROCESSING'].includes(q.status))
        if (autoRefreshRef.current) { clearInterval(autoRefreshRef.current); autoRefreshRef.current = null }
        if (hasActive) { autoRefreshRef.current = setInterval(fetchQueries, 30000) }
        return () => { if (autoRefreshRef.current) clearInterval(autoRefreshRef.current) }
    }, [queries, fetchQueries])

    const handleSubmitQuery = async () => {
        if (!selectedAccountId || !startDate || !endDate) return
        setSubmitting(true)
        setError('')
        try {
            const fmt = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
            await amazonAPI.queries.submit(selectedAccountId, {
                start_date: fmt(startDate), end_date: fmt(endDate), exclude_inactive: excludeInactive
            })
            setCurrentPage(1)
            fetchQueries()
        } catch (err) { setError(formatErrorResponse(err)) }
        finally { setSubmitting(false) }
    }

    const handleRetry = async (queryId) => {
        setError('')
        try { await amazonAPI.queries.retry(queryId); fetchQueries() }
        catch (err) { setError(formatErrorResponse(err)) }
    }

    const handleDeleteQuery = async (query) => {
        if (!window.confirm('Delete this query and all its economics records?')) return
        setError('')
        try {
            await amazonAPI.queries.delete(query.id)
            fetchQueries()
        } catch (err) { setError(formatErrorResponse(err)) }
    }

    const getCurrentFilters = () => {
        const params = new URLSearchParams()
        if (selectedAccountId) params.set('account', selectedAccountId)
        if (filterStartDate) params.set('filterStart', filterStartDate)
        if (filterEndDate) params.set('filterEnd', filterEndDate)
        if (currentPage > 1) params.set('page', currentPage)
        if (sortDirection !== 'desc') params.set('sortDir', sortDirection)
        if (sourceFilter) params.set('source', sourceFilter)
        const search = params.toString()
        return search ? '?' + search : ''
    }

    const handleSort = (_column, direction) => {
        setSortDirection(direction)
        setCurrentPage(1)
    }

    const queryColumns = [
        { id: 'account', header: 'Account', cell: (row) => row.seller_account_name || '' },
        { id: 'dateRange', header: 'Date Range', sortable: true, cell: (row) => {
            const start = row.data_start_date ? formatDateOnly(row.data_start_date) : ''
            const end = row.data_end_date ? formatDateOnly(row.data_end_date) : ''
            return start && end ? `${start} – ${end}` : ''
        }},
        { id: 'status', header: 'Status', cell: (row) => getStatusBadge(row.status) },
        { id: 'source', header: 'Source', cell: (row) => row.collection_type === 'daily'
            ? <Badge bg="info">Auto</Badge>
            : <Badge bg="secondary">Manual</Badge>
        },
        { id: 'type', header: 'Type', cell: (row) => row.query_type || '' },
        { id: 'submittedAt', header: 'Submitted At', cell: (row) => formatDateOnly(row.requested_at) },
        { id: 'actions', header: 'Actions', cell: (row) => (
            <>
                {row.status === 'DONE' && (
                    <Button variant="primary" size="sm" className="me-2"
                        onClick={(e) => { e.stopPropagation(); navigate(`/amazon-economics/${row.id}`, { state: { returnFilters: getCurrentFilters() } }) }}>
                        Details
                    </Button>
                )}
                {row.status === 'FAILED' && hasPermission('amazon.manage') && (
                    <Button variant="outline-warning" size="sm" className="me-2"
                        onClick={(e) => { e.stopPropagation(); handleRetry(row.id) }}>Retry</Button>
                )}
                {['DONE', 'FAILED'].includes(row.status) && hasPermission('amazon.manage') && (
                    <Button variant="outline-danger" size="sm"
                        onClick={(e) => { e.stopPropagation(); handleDeleteQuery(row) }}>Delete</Button>
                )}
            </>
        )}
    ]

    return (
        <Container fluid className="px-4 py-4">
            <h1 className="mb-3">SKU Economics</h1>

            {error && <div className="alert alert-danger">{error}</div>}

            <div style={{ maxWidth: '900px' }}>
                <div className="d-flex align-items-end gap-3 flex-wrap mb-2">
                    <div style={{ minWidth: '220px', flex: 1 }}>
                        <Form.Label>Amazon Account</Form.Label>
                        <Select
                            value={accountOptions.find(opt => opt.value === selectedAccountId) || null}
                            onChange={(opt) => { setSelectedAccountId(opt?.value || ''); setCurrentPage(1) }}
                            options={accountOptions}
                            isClearable
                            placeholder="All Accounts..."
                            className="react-select-container"
                            classNamePrefix="react-select"
                        />
                    </div>
                    <div style={{ width: '150px' }}>
                        <Form.Label>Start Date</Form.Label>
                        <DatePicker
                            selected={startDate}
                            onChange={(date) => { setStartDate(date); setActiveQuickDate(null) }}
                            className="form-control"
                            dateFormat="MM/dd/yyyy"
                            selectsStart startDate={startDate} endDate={endDate} maxDate={endDate}
                            showMonthDropdown showYearDropdown dropdownMode="select" portalId="root"
                        />
                    </div>
                    <div style={{ width: '150px' }}>
                        <Form.Label>End Date</Form.Label>
                        <DatePicker
                            selected={endDate}
                            onChange={(date) => { setEndDate(date); setActiveQuickDate(null) }}
                            className="form-control"
                            dateFormat="MM/dd/yyyy"
                            selectsEnd startDate={startDate} endDate={endDate} minDate={startDate}
                            maxDate={new Date()} openToDate={startDate}
                            showMonthDropdown showYearDropdown dropdownMode="select" portalId="root"
                        />
                    </div>
                    <div className="d-flex gap-1 align-items-center" style={{ paddingBottom: '4px' }}>
                        {[{ label: '1M', months: 1 }, { label: '3M', months: 3 }, { label: '6M', months: 6 }].map(({ label, months }) => (
                            <Badge key={months} bg={activeQuickDate === months ? 'primary' : 'light'}
                                style={{ cursor: 'pointer', fontSize: '0.8rem' }}
                                onClick={() => {
                                    const end = new Date(); const start = new Date()
                                    start.setMonth(end.getMonth() - months)
                                    setStartDate(start); setEndDate(end); setActiveQuickDate(months)
                                }}>{label}</Badge>
                        ))}
                    </div>
                    {hasPermission('amazon.manage') && (
                        <div>
                            <Button variant="success" onClick={handleSubmitQuery}
                                disabled={submitting || !selectedAccountId || !startDate || !endDate}>
                                {submitting ? (<><Spinner size="sm" className="me-2" />Submitting...</>) : 'Submit Query'}
                            </Button>
                        </div>
                    )}
                </div>
                <Form.Check type="checkbox" label="Exclude items with no activity"
                    checked={excludeInactive} onChange={(e) => setExcludeInactive(e.target.checked)}
                    style={{ fontSize: '0.85rem' }} />
            </div>

            <hr className="my-4" />

            <div>
                <div className="d-flex justify-content-between align-items-center mb-3">
                    <h4 className="mb-0">Query Requests</h4>
                    <Button variant="outline-secondary" size="sm" onClick={fetchQueries} disabled={queriesLoading}>
                        <i className="bi bi-arrow-clockwise me-1"></i>Refresh
                    </Button>
                </div>

                <div className="d-flex mb-3">
                    <div className="d-flex gap-2 rounded p-2" style={{ background: 'var(--bs-tertiary-bg)' }}>
                        <Form.Group>
                            <Form.Label className="mb-1" style={{ fontSize: '0.8rem' }}>Period Start</Form.Label>
                            <Form.Control type="date" size="sm" value={filterStartDate}
                                onChange={(e) => { setFilterStartDate(e.target.value); setCurrentPage(1) }}
                                autoComplete="off" spellCheck="false" />
                        </Form.Group>
                        <Form.Group>
                            <Form.Label className="mb-1" style={{ fontSize: '0.8rem' }}>Period End</Form.Label>
                            <Form.Control type="date" size="sm" value={filterEndDate}
                                onChange={(e) => { setFilterEndDate(e.target.value); setCurrentPage(1) }}
                                autoComplete="off" spellCheck="false" />
                        </Form.Group>
                        <Form.Group>
                            <Form.Label className="mb-1" style={{ fontSize: '0.8rem' }}>Source</Form.Label>
                            <Form.Select size="sm" value={sourceFilter}
                                onChange={(e) => { setSourceFilter(e.target.value); setCurrentPage(1) }}>
                                <option value="">All</option>
                                <option value="on_demand">Manual</option>
                                <option value="daily">Auto</option>
                            </Form.Select>
                        </Form.Group>
                    </div>
                </div>

                {queriesLoading && queries.length === 0 ? (
                    <div className="text-center py-4"><Spinner animation="border" /></div>
                ) : queries.length === 0 ? (
                    <p className="text-muted">No queries found.</p>
                ) : (
                    <>
                        <div className="table-responsive">
                            <ResizableTable columns={queryColumns} data={queries} tableId="amazon_queries"
                                enableSorting={true} onSort={handleSort}
                                sortColumn="dateRange" sortDirection={sortDirection}
                                initialColumnWidths={{ dateRange: 220, status: 120, submittedAt: 150, actions: 220 }}
                                minHeight="auto" striped hover className="table-sm" />
                        </div>
                        <div className="mt-3">
                            <PaginationComponent
                                currentPage={currentPage}
                                totalItems={totalItems}
                                onPageChange={setCurrentPage}
                                onItemsPerPageChange={setPageSize}
                            />
                        </div>
                    </>
                )}
            </div>
        </Container>
    )
}

export default AmazonEconomicsListScreen
